import { useEffect, useState } from 'react'
import { useApp } from '../context/AppContext'
import useModal from '../hooks/useModal'
import Modal from '../components/Shared/Modal'
import AddPriority from '../components/priority/AddPriority'
import EditPriority from '../components/priority/EditPriority'
import { AnimatePresence, motion } from 'framer-motion'
import { sileo } from 'sileo'
import SettingsSection from '../components/Settings/SettingsSection'
import { getPriorityMeta } from '../utils/priorityMatrix'
import '../styles/data-table.css'

const TicketPriorities = () => {
  const { supabase } = useApp()

  const [priorities, setPriorities] = useState([])
  const [loading, setLoading] = useState(false)

  const {
    openModal,
    isOpen: isModalOpen,
    modalConfig,
    closeModal,
    isLargeModal,
  } = useModal()

  // ==========================================
  // LOAD
  // ==========================================
  const loadPriorities = async () => {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .schema('Tickets')
        .from('priority')
        .select('*')
        .order('level', { ascending: false })

      if (error) throw error

      setPriorities(data || [])
    } catch (error) {
      console.error(error)
      sileo.error({
        title: 'Error',
        description: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPriorities()
  }, [])

  // ==========================================
  // CREATE
  // ==========================================
  const handleAdded = async () => {
    await loadPriorities()
    closeModal()
  }

  // ==========================================
  // UPDATE
  // ==========================================
  const handleUpdatePriority = async (id, payload) => {
    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('priority')
        .update(payload)
        .eq('id', id)

      if (error) throw error

      sileo.success({
        title: 'Prioridad actualizada',
        description: 'Los cambios se guardaron correctamente.',
      })

      closeModal()
      loadPriorities()
    } catch (error) {
      console.error(error)
      sileo.error({
        title: 'Error',
        description: 'No se pudo actualizar la prioridad.',
      })
    }
  }

  // ==========================================
  // EDIT MODAL
  // ==========================================
  const handleEdit = (priority) => {
    openModal(
      'Editar Prioridad',
      <EditPriority priority={priority} onSave={handleUpdatePriority} />
    )
  }

  // ==========================================
  // STYLES
  // ==========================================
  const maxLevel =
    priorities.length > 0 ? Math.max(...priorities.map((p) => p.level)) : 0

  /** Color de la prioridad: catálogo 1..4 o, si no, según su nivel. */
  const getColor = (p) => {
    if (p.id >= 1 && p.id <= 4) return getPriorityMeta(p.id).color
    return p.level === maxLevel ? 'var(--gm-danger)' : 'var(--text-2)'
  }

  const openAdd = () =>
    openModal('Agregar nivel', <AddPriority onAdded={handleAdded} />)

  return (
    <div>
      {/* MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={closeModal}
            title={modalConfig.title}
            isLarge={isLargeModal}
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {modalConfig.component}
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      <SettingsSection
        title="Prioridades"
        icon="fa-flag"
        description="Niveles de criticidad de los tickets: a mayor número, mayor urgencia."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={openAdd}
          >
            <i className="fa-solid fa-plus me-2" />
            Nuevo nivel
          </button>
        }
      >
        <div className="tt" style={{ flex: 'none' }}>
          <div className="tt-scroll">
            <table className="tt-table">
              <thead>
                <tr>
                  <th>Prioridad</th>
                  <th style={{ width: 140 }}>Nivel</th>
                  <th style={{ width: 60 }} aria-label="Acciones" />
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="tt-empty">
                      <span className="spinner-border spinner-border-sm text-primary" />
                    </td>
                  </tr>
                ) : priorities.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="tt-empty">
                      <i className="fa-solid fa-flag d-block fs-5 mb-2 opacity-50" />
                      No hay prioridades configuradas.
                    </td>
                  </tr>
                ) : (
                  priorities.map((p) => {
                    const color = getColor(p)
                    return (
                      <tr
                        key={p.id}
                        className="tt-row"
                        style={{ cursor: 'default' }}
                      >
                        <td>
                          <span className="d-inline-flex align-items-center gap-2">
                            <span
                              className="tt-dot"
                              style={{ background: color }}
                            />
                            <span className="fw-semibold">{p.name}</span>
                            {p.level === maxLevel && (
                              <i
                                className="fa-solid fa-fire"
                                style={{ color, fontSize: '0.72rem' }}
                                title="Nivel más crítico"
                              />
                            )}
                          </span>
                        </td>
                        <td>
                          <span
                            className="tt-badge gm-tono"
                            style={{ '--tono': color }}
                          >
                            <span className="tt-muted fw-normal">Nivel</span>
                            {p.level}
                          </span>
                        </td>
                        <td>
                          <div className="tt-actions">
                            <button
                              type="button"
                              className="tt-icon-btn"
                              title="Editar"
                              aria-label={`Editar prioridad ${p.name}`}
                              onClick={() => handleEdit(p)}
                            >
                              <i className="fa-solid fa-pen" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </SettingsSection>
    </div>
  )
}

export default TicketPriorities
